import { type AllMessages, type GetMessages, TranslationStringStatus } from './types';
import React, { useEffect, useMemo, useState } from 'react';
import { IntlProvider as ReactIntlProvider } from 'react-intl';
import { useIntlProvider } from './useIntlProvider';
import { useLocaleMessages } from './useLocaleMessages';
import { useParentIntlProvider } from './useParentIntlProvider';

interface IntlProviderProps {
  /** Default locale for the IntlProvider. `useIntlProvider` defaults it to "en-US". */
  defaultLocale?: string;
  /**
   * Locale for the IntlProvider. When omitted, will automatically use the locale of a parent react-intl IntlProvider, if it exists. When passed in, overrides the parent provider's locale.
   *
   * Must be passed in if the the parent context uses a library other than react-intl for its internationalization provider or if no parent internationalization context exists. */
  locale?: string;
  /** Messages for all supported locales */
  messages?: AllMessages;
  /** Get Messages for the locale passed as the argument. It is a function that returns a promise of a dictionary of messages */
  getMessages?: GetMessages;
  /** Children to internationalize */
  children: React.ReactNode;
}

/**
 * Wraps a react-intl IntlProvider and augments it with extra features and fallback behavior.
 *
 * Gets info from a parent internationalization context if it exists, then delegates all logic to `useIntlProvider`.
 */
export const IntlProvider = ({
  children,
  defaultLocale,
  locale,
  messages,
  getMessages,
  ...props
}: IntlProviderProps): JSX.Element => {
  const [intlMessages, setIntlMessages] = useState<AllMessages>(messages as AllMessages);
  const intlConfig = { defaultLocale, locale, messages: intlMessages };
  const parentIntlConfig = useParentIntlProvider();
  const intlProviderProps = useMemo(
    () => useIntlProvider(intlConfig, parentIntlConfig),
    [...Object.values(intlConfig), ...Object.values(parentIntlConfig)],
  );
  const [status, localeMessages] = useLocaleMessages(getMessages, {
    locale: intlProviderProps.locale,
    defaultLocale: intlProviderProps.defaultLocale,
    parentLocale: parentIntlConfig.locale,
    parentDefaultLocale: parentIntlConfig.defaultLocale,
  });
  useEffect(() => {
    if (localeMessages) {
      setIntlMessages(localeMessages);
    }
  }, [localeMessages]);
  const hasMessages = !getMessages || intlMessages;
  const isErroredOrTimedOut = [
    TranslationStringStatus.ERRORED,
    TranslationStringStatus.TIMED_OUT,
  ].includes(status); // for Errored/timeout cases, we want the fallback to english strings.
  return hasMessages || isErroredOrTimedOut ? (
    <ReactIntlProvider {...intlProviderProps} {...props}>
      {children}
    </ReactIntlProvider>
  ) : (
    <></>
  );
};
